import React, { useEffect, useState } from 'react';
import { researchosService } from '../../services/researchosService';
import { useSortableRows, SortableTh } from '../../components/tables/SortableTable';

const STATE = {
  ok: ['Arrived', 'text-green-500'], ok_nothing_new: ['Ran, nothing new', 'text-green-500'], not_started: ['Not started', 'text-red-500'], started_no_push: ['Started, no push', 'text-amber-500'],
  pending: ['Waiting for the slot', 'text-gray-400'], not_expected: ['Nothing expected', 'text-gray-400'],
};
const when = s => (s ? new Date(s).toLocaleString('en-GB', { timeZone: 'Asia/Dubai', hour12: false }).replace(',', '') + ' Dubai' : '—');

const RUN_COLS = [
  { key: 'started', type: 'string', get: r => r.started_at },
  { key: 'minutes', type: 'number', get: r => (r.minutes == null ? -1 : r.minutes) },
  { key: 'items', type: 'number', get: r => r.items.posts ?? r.items.sources ?? 0 },
];
const runWhen = s => new Date(s).toLocaleString('en-GB', { timeZone: 'Asia/Dubai', hour12: false, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(',', '');
const length = r => (r.minutes != null ? `${r.minutes} min` : r.status === 'running' ? 'running' : 'no done ping');

// Each collector's recent runs and how long they took (2026-10-17): start ping → the collector's own done / error ping.
function RunTable({ runs }) {
  const { sorted, toggle, indicator } = useSortableRows(runs, RUN_COLS);
  if (!runs || !runs.length) return null;
  const latestTimed = runs.find(r => r.slowest.length);   // runs arrive newest first
  return (
    <div className="mt-2 overflow-x-auto scrollbar-thin">
      <table className="w-full text-xs tabular-nums">
        <thead className="text-[10px] uppercase tracking-wider text-gray-400">
          <tr>
            <SortableTh label="Started (Dubai)" colKey="started" toggle={toggle} indicator={indicator} align="left" />
            <SortableTh label="Took" colKey="minutes" toggle={toggle} indicator={indicator} />
            <SortableTh label="Items" colKey="items" toggle={toggle} indicator={indicator} />
          </tr>
        </thead>
        <tbody>
          {sorted.map(r => (
            <tr key={r.id} className="border-t border-gray-100 dark:border-gray-800"
                title={r.slowest.length ? 'slowest: ' + r.slowest.map(x => `${x.source} ${Math.round(x.seconds)}s`).join(', ') : ''}>
              <td className="py-0.5 text-left text-gray-600 dark:text-gray-300">{runWhen(r.started_at)}</td>
              <td className={'py-0.5 text-right ' + (r.status === 'failed' ? 'text-red-500' : 'text-gray-700 dark:text-gray-200')}>{length(r)}</td>
              <td className="py-0.5 text-right text-gray-500">{r.items.posts ?? r.items.sources ?? '—'}</td>
            </tr>))}
        </tbody>
      </table>
      {latestTimed && (
        <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
          slowest ({runWhen(latestTimed.started_at)}): {latestTimed.slowest.map(x => `${x.source} ${Math.round(x.seconds)}s`).join(' · ')}
        </div>)}
    </div>
  );
}

// The collectors' heartbeat (wish #22): the platform never reaches the mini; it knows each collector's cadence and
// sees its pushes. The same rows the admin digest email is written from.
export default function CollectorsPanel() {
  const [rows, setRows] = useState(null);
  useEffect(() => { researchosService.getCollectors().then(r => setRows(r.data.collectors || [])).catch(() => setRows([])); }, []);
  if (rows === null) return null;
  return (
    <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-800">
      <div className="mb-2 text-[11px] uppercase tracking-[0.15em] text-gray-400">Collectors · heartbeat</div>
      <div className="grid gap-2 md:grid-cols-3">
        {rows.map(r => {
          const [label, cls] = STATE[r.state] || [r.state, ''];
          return (
            <div key={r.collector} className="rounded border border-gray-100 p-3 text-sm dark:border-gray-800">
              <div className="font-medium text-gray-900 dark:text-gray-100">{r.label}</div>
              <div className={'text-xs ' + cls}>{label}{r.slot_date ? ` · slot ${r.slot_date}` : ''}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">last push {when(r.last_push)}{r.started_at ? ` · started ${when(r.started_at)}` : ''}</div>
              {r.error && <div className="text-xs text-red-500">{String(r.error).slice(0, 160)}</div>}
              <RunTable runs={r.history || []} />
            </div>);
        })}
      </div>
    </div>
  );
}
